import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faEllipsisVertical, faPeriod } from '@fortawesome/pro-solid-svg-icons'
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome'
import { Tooltip } from '@lib/tooltip'
import { ButtonHTMLAttributes, DetailedHTMLProps, FC, useId } from 'react'
import { twMerge } from '@lib/tailwind-merge'

type Props = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  annotate?: boolean
  iconProps?: Omit<FontAwesomeIconProps, 'icon'>
  icon?: IconDefinition
  srOnlyLabel?: string
  tooltip?: string
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, 'id'>

  /**
   * Anchor to a <Tooltip> rendered by the caller instead of rendering one of
   * our own, showing `tooltip` as its content.
   *
   * Every <Tooltip> registers a MutationObserver over the whole of
   * `document.body`, so a button repeated once per row of a large table has to
   * share one: a thousand rows otherwise means a thousand observers all
   * re-scanning the document every time the table re-renders.
   */
  tooltipId?: string

  // If true, the button will be a circle regardless of the theme's rounded-primary
  circle?: boolean
}

export const IconButton: FC<Props> = ({
  annotate,
  onClick,
  className,
  icon = faEllipsisVertical,
  iconProps,
  srOnlyLabel,
  tooltip,
  tooltipId,
  tooltipProps,
  circle = false,
  ...props
}) => {
  const ownId = useId()
  const anchorId = tooltipId ?? ownId

  return (
    <>
      {tooltip && !tooltipId && (
        <Tooltip id={anchorId} {...tooltipProps}>
          {tooltip}
        </Tooltip>
      )}
      <button
        {...props}
        onClick={onClick}
        className={twMerge(
          'w-6 h-6 group hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
          className,
          circle ? 'rounded-full' : 'rounded-primary',
        )}
        data-tooltip-id={anchorId}
        data-tooltip-content={tooltipId ? tooltip : undefined}
      >
        {srOnlyLabel && <label className="sr-only">{srOnlyLabel}</label>}
        <span className="fa-layers fa-fw">
          <FontAwesomeIcon {...iconProps} icon={icon} />
          {annotate && (
            <>
              <FontAwesomeIcon
                icon={faPeriod}
                size="2x"
                transform="up-9.4 right-4.5"
                inverse
              />
              <FontAwesomeIcon
                className="text-primary-600"
                icon={faPeriod}
                size="xl"
                transform="up-11 right-6"
              />
            </>
          )}
        </span>
      </button>
    </>
  )
}
